import React from "react";
import styled from "styled-components";
import _ from "lodash";

import { Select } from "src/library/components/Forms";
import { useProductsApi } from "src/core/products/api/products.api";
import { IMinimumOrderQuantitySetting } from "src/core/drops/api/drops.api";
import { sentenceCase } from "src/library/utils";
import { Text } from "src/library/components/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";

export interface AddNewMinimumOrderQuantitySettingProps {
  minQtySettings: IMinimumOrderQuantitySetting;
  updateQtySettings: (code: string) => void;
}

/**
 * Renders a select input for adding a new minimum order quantity setting and
 * a message if all available pack sizes have already been set.
 *
 * When an option is selected, the `updateQtySettings` callback is called and this component is re-rendered.
 * Meaning, the select input will not show a selected value as it will be updated on change.
 * @returns
 */
const AddNewMinimumOrderQuantitySetting: React.FunctionComponent<AddNewMinimumOrderQuantitySettingProps> = ({ minQtySettings, updateQtySettings }) => {
  const [ getDistinctPackSizeCodesRequestResponse, getDistinctPackSizeCodesRequest ] = useProductsApi("GET_DISTINCT_PACK_SIZE_CODES");

  React.useEffect(() => {
    getDistinctPackSizeCodesRequest(null);
  }, [ getDistinctPackSizeCodesRequest ]);

  // Set the distinct pack size codes from the request response
  const distinctPackSizeCodes = React.useMemo(() => {
    if (getDistinctPackSizeCodesRequestResponse.data?.length) {
      return getDistinctPackSizeCodesRequestResponse.data;
    }

    return [];
  }, [ getDistinctPackSizeCodesRequestResponse.data ]);

  // Get the pack size codes that have not been set as options for the select input
  const unsetAvailablePackSizeCodes = React.useMemo(() => {
    if (!distinctPackSizeCodes.length) {
      return [];
    }

    const codesNotSet = _.difference(
      distinctPackSizeCodes,
      Object.keys(minQtySettings)
    );

    return codesNotSet.map(code => ({
      text: sentenceCase(code),
      value: code
    }));
  }, [ distinctPackSizeCodes, minQtySettings ]);

  return (
    <StyledWrapper>
      {getDistinctPackSizeCodesRequestResponse.pending && (
        <div className="loader-container">
          <LoadingSpinner
            type="flat"
            position="inline"
            size={10}
          />
        </div>
      )}

      {!getDistinctPackSizeCodesRequestResponse.pending && (
        !unsetAvailablePackSizeCodes.length ?
          distinctPackSizeCodes.length ? (
            <Text>
              All available pack sizes have been set.
            </Text>
          ) :
            null :
          (
            <div className="select-container">
              <Select
                name="unset-available-pack-size-codes"
                label="Pack sizes not yet set"
                autoHideLabels
                handleChange={e => updateQtySettings(e.target.value)}
                options={unsetAvailablePackSizeCodes}
              // Value always not set as we update available pack size codes on change
              // Therefore we won't ever need to see a selected value displayed in the select
                value=""
                unselectedOption={{
                  text: `Pack size${unsetAvailablePackSizeCodes.length > 1 ? "s" : ""} not yet set`,
                  value: ""
                }}
              />
            </div>
          )
      )}

    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  height: 40px;
  max-width: 400px;
  display: flex;
  align-items: center;

  .loader-container {
    width: 100%;
    justify-content: center;
  }

  .select-container {
    width: 100%;
  }
`;

export default AddNewMinimumOrderQuantitySetting;